// app/src/lib/scale/finixForm.js — Finix hosted card tokenization (PCI-safe).
//
// Loads Finix.js, mounts hosted card fields into a container element, and
// tokenizes to a one-time token (TK…) on submit. The raw card never touches
// Kanjo — Finix's iframe fields collect + tokenize it. The resulting token is
// posted to /subscription/create.
//
// Requires window.__KANJO_CONFIG__.finixApplicationId + finixEnv (injected by
// deploy.sh from the kanjo/<stage>/finix secret). NOT unit-tested (depends on
// the third-party iframe SDK) — verified manually in sandbox.

import { getConfig } from '../config.js';

const FINIX_JS = 'https://js.finix.com/v/1/finix.js';
let loaderPromise = null;

/** Load Finix.js once; resolves to window.Finix. */
export function loadFinix() {
  if (typeof window !== 'undefined' && window.Finix) return Promise.resolve(window.Finix);
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = FINIX_JS;
    s.async = true;
    s.onload = () => (window.Finix ? resolve(window.Finix) : reject(new Error('Finix.js loaded but window.Finix missing')));
    s.onerror = () => { loaderPromise = null; reject(new Error('failed to load Finix.js')); };
    document.head.appendChild(s);
  });
  return loaderPromise;
}

/**
 * Mount hosted card fields into `containerId`. Returns `{ tokenize, unmount }`.
 * `tokenize()` resolves to a one-time card token (string) or rejects on error.
 */
export async function mountCardForm(containerId, { onUpdate } = {}) {
  const Finix = await loadFinix();
  const cfg = getConfig();
  if (!cfg.finixApplicationId) throw new Error('finix: applicationId not configured');
  const env = cfg.finixEnv || 'sandbox';

  // Finix.CardTokenForm renders the hosted fields (number/expiry/cvv) as iframes.
  const form = Finix.CardTokenForm(containerId, {
    showAddress: false,
    onUpdate: typeof onUpdate === 'function' ? onUpdate : undefined,
  });

  return {
    /** @returns {Promise<string>} the tokenized card id (TK…) */
    tokenize() {
      return new Promise((resolve, reject) => {
        try {
          form.submit(env, cfg.finixApplicationId, (err, res) => {
            if (err) return reject(err instanceof Error ? err : new Error('card tokenization failed'));
            const token = res && res.data && res.data.id;
            if (!token) return reject(new Error('no token returned by Finix'));
            resolve(token);
          });
        } catch (e) { reject(e instanceof Error ? e : new Error('card tokenization failed')); }
      });
    },
    unmount() { try { form && form.unmount && form.unmount(); } catch { /* ignore */ } },
  };
}
